import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  getProjects, getCustomers, uploadDocumentation, uploadDocumentationMulti, importFromGithub,
  importFromAzureDevOps, importFromBitbucket, importFromGitlab, getJobStatus
} from '../services/api';
import { toast } from 'sonner';
import {
  Upload, Loader2, ArrowLeft, Cloud, GitBranch, Link, KeyRound, Cpu,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import JobProgressTracker from '../components/upload/JobProgressTracker';
import FileUploadTab from '../components/upload/FileUploadTab';
import { uploadingStatus } from '../utils/slimZip';

// Provider metadata for the optional repo section of the multi-source upload.
// Mirrors the labels / placeholders used by the dedicated repo import tabs.
const REPO_PROVIDERS = {
  'github': {
    label: 'GitHub',
    urlPlaceholder: 'https://github.com/{owner}/{repo}',
    tokenLabel: 'Access Token (optional)',
    tokenPlaceholder: 'GitHub token for private repositories',
    needsUsername: false,
  },
  'azure-devops': {
    label: 'Azure DevOps',
    urlPlaceholder: 'https://dev.azure.com/{org}/{project}/_git/{repo}',
    tokenLabel: 'Personal Access Token (PAT)',
    tokenPlaceholder: 'Your Azure DevOps PAT with Code (Read) scope',
    needsUsername: false,
  },
  'bitbucket': {
    label: 'Bitbucket',
    urlPlaceholder: 'https://bitbucket.org/{workspace}/{repo}',
    tokenLabel: 'App Password',
    tokenPlaceholder: 'Bitbucket App Password',
    needsUsername: true,
  },
  'gitlab': {
    label: 'GitLab',
    urlPlaceholder: 'https://gitlab.com/{owner}/{repo}',
    tokenLabel: 'Personal Access Token (optional)',
    tokenPlaceholder: 'GitLab personal access token',
    needsUsername: false,
  },
};

const UploadPage = () => {
  const { canUpload } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedProject = searchParams.get('project');

  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedProject, setSelectedProject] = useState(preselectedProject || '');
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(true);
  // File Upload tab holds MANY files (zips included) merged into ONE document
  const [files, setFiles] = useState([]);

  // Repo import fields (dedicated repo tabs)
  const [repoUrl, setRepoUrl] = useState('');
  const [patToken, setPatToken] = useState('');
  const [branch, setBranch] = useState('');
  const [bbUsername, setBbUsername] = useState('');

  // Optional repo linked to the multi-source file upload (kept separate from the repo tabs)
  const [showMultiRepo, setShowMultiRepo] = useState(false);
  const [multiRepoProvider, setMultiRepoProvider] = useState('github');
  const [multiRepoUrl, setMultiRepoUrl] = useState('');
  const [multiRepoBranch, setMultiRepoBranch] = useState('');
  const [multiRepoToken, setMultiRepoToken] = useState('');
  const [multiRepoUsername, setMultiRepoUsername] = useState('');

  // Job tracking
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobStartTime, setJobStartTime] = useState(null);
  const [jobElapsedMin, setJobElapsedMin] = useState(0);
  const pollInterval = useRef(null);
  const elapsedTimer = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [customersRes, projectsRes] = await Promise.all([
        getCustomers(),
        getProjects()
      ]);
      setCustomers(customersRes.data);
      setProjects(projectsRes.data);

      if (preselectedProject) {
        const proj = projectsRes.data.find(p => p.id === preselectedProject);
        if (proj) {
          setSelectedCustomer(proj.customer_id);
          setSelectedProject(proj.id);
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [preselectedProject]);

  useEffect(() => {
    if (!canUpload) {
      navigate('/documentation');
      return;
    }
    fetchData();
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
      if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    };
  }, [canUpload, navigate, fetchData]);

  const filteredProjects = selectedCustomer
    ? projects.filter(p => p.customer_id === selectedCustomer)
    : projects;

  const startPolling = useCallback((jId) => {
    if (pollInterval.current) clearInterval(pollInterval.current);
    if (elapsedTimer.current) clearInterval(elapsedTimer.current);

    const startTime = Date.now();
    setJobStartTime(startTime);
    setJobElapsedMin(0);

    // Track elapsed minutes
    elapsedTimer.current = setInterval(() => {
      setJobElapsedMin(Math.floor((Date.now() - startTime) / 60000));
    }, 30000);

    pollInterval.current = setInterval(async () => {
      try {
        const res = await getJobStatus(jId);
        setJobStatus(res.data);

        if (res.data.status === 'complete') {
          clearInterval(pollInterval.current);
          clearInterval(elapsedTimer.current);
          pollInterval.current = null;
          elapsedTimer.current = null;
          toast.success('Documentation generated successfully!');
        } else if (res.data.status === 'failed') {
          clearInterval(pollInterval.current);
          clearInterval(elapsedTimer.current);
          pollInterval.current = null;
          elapsedTimer.current = null;
          toast.error(res.data.error || 'Documentation generation failed');
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 3000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- getJobStatus is a stable module import

  const handleFileUpload = async () => {
    const linkedRepoUrl = multiRepoUrl.trim();
    const hasRepo = !!linkedRepoUrl;

    if (!selectedProject || !title || (files.length === 0 && !hasRepo)) {
      toast.error('Please fill in all required fields and select at least one file or link a repository');
      return;
    }

    const formData = new FormData();
    formData.append('project_id', selectedProject);
    formData.append('title', title);
    formData.append('tags', tags);

    // One file and no linked repo -> keep the original single-source upload path
    const singleSource = files.length === 1 && !hasRepo;
    if (singleSource) {
      formData.append('file', files[0]);
    } else {
      // All files go under the SAME key so the backend merges them into ONE document
      files.forEach((f) => formData.append('files', f));
      if (hasRepo) {
        formData.append('repo_provider', multiRepoProvider);
        formData.append('repo_url', linkedRepoUrl);
        if (multiRepoBranch.trim()) formData.append('repo_branch', multiRepoBranch.trim());
        if (multiRepoToken) formData.append('repo_token', multiRepoToken);
        if (REPO_PROVIDERS[multiRepoProvider].needsUsername && multiRepoUsername.trim()) {
          formData.append('repo_username', multiRepoUsername.trim());
        }
      }
    }

    try {
      setJobStatus({
        status: 'uploading',
        progress: 0,
        message: singleSource ? 'Uploading file...' : 'Uploading sources...'
      });
      // Percent, MB sent and time left go onto the job card while it is still uploading
      const onProgress = (evt) => {
        setJobStatus((s) => (s?.status === 'uploading' ? uploadingStatus(evt) : s));
      };
      const response = singleSource
        ? await uploadDocumentation(formData, onProgress)
        : await uploadDocumentationMulti(formData, onProgress);
      const jId = response.data.job_id;
      setJobId(jId);
      setJobStatus({ status: 'processing', progress: 5, message: 'Job started...' });
      startPolling(jId);
    } catch (error) {
      setJobStatus(null);
      setJobId(null);
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const handleAzureDevOpsImport = async () => {
    if (!selectedProject || !title || !repoUrl) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setJobStatus({ status: 'uploading', progress: 0, message: 'Starting import...' });
      const response = await importFromAzureDevOps({
        repo_url: repoUrl,
        project_id: selectedProject,
        title: title,
        pat_token: patToken || undefined,
        branch: branch || undefined,
        tags: tags
      });
      const jId = response.data.job_id;
      setJobId(jId);
      setJobStatus({ status: 'processing', progress: 5, message: 'Cloning repository...' });
      startPolling(jId);
    } catch (error) {
      setJobStatus(null);
      setJobId(null);
      toast.error(error.response?.data?.detail || 'Import failed');
    }
  };

  const handleGithubImport = async () => {
    if (!selectedProject || !title || !repoUrl) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setJobStatus({ status: 'uploading', progress: 0, message: 'Starting import...' });
      const response = await importFromGithub({
        github_url: repoUrl,
        project_id: selectedProject,
        title: title,
        github_token: patToken || undefined,
        branch: branch || undefined,
        tags: tags
      });
      const jId = response.data.job_id;
      setJobId(jId);
      setJobStatus({ status: 'processing', progress: 5, message: 'Cloning repository...' });
      startPolling(jId);
    } catch (error) {
      setJobStatus(null);
      setJobId(null);
      toast.error(error.response?.data?.detail || 'Import failed');
    }
  };

  const handleBitbucketImport = async () => {
    if (!selectedProject || !title || !repoUrl) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setJobStatus({ status: 'uploading', progress: 0, message: 'Starting import...' });
      const response = await importFromBitbucket({
        repo_url: repoUrl,
        project_id: selectedProject,
        title: title,
        username: bbUsername || undefined,
        app_password: patToken || undefined,
        branch: branch || undefined,
        tags: tags
      });
      const jId = response.data.job_id;
      setJobId(jId);
      setJobStatus({ status: 'processing', progress: 5, message: 'Cloning repository...' });
      startPolling(jId);
    } catch (error) {
      setJobStatus(null);
      setJobId(null);
      toast.error(error.response?.data?.detail || 'Import failed');
    }
  };

  const handleGitlabImport = async () => {
    if (!selectedProject || !title || !repoUrl) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setJobStatus({ status: 'uploading', progress: 0, message: 'Starting import...' });
      const response = await importFromGitlab({
        repo_url: repoUrl,
        project_id: selectedProject,
        title: title,
        gitlab_token: patToken || undefined,
        branch: branch || undefined,
        tags: tags
      });
      const jId = response.data.job_id;
      setJobId(jId);
      setJobStatus({ status: 'processing', progress: 5, message: 'Cloning repository...' });
      startPolling(jId);
    } catch (error) {
      setJobStatus(null);
      setJobId(null);
      toast.error(error.response?.data?.detail || 'Import failed');
    }
  };

  const resetJob = () => {
    if (pollInterval.current) clearInterval(pollInterval.current);
    setJobId(null);
    setJobStatus(null);
    setFiles([]);
    setRepoUrl('');
    setPatToken('');
    setBbUsername('');
    setMultiRepoUrl('');
    setMultiRepoBranch('');
    setMultiRepoToken('');
    setMultiRepoUsername('');
    setShowMultiRepo(false);
    setTitle('');
    setTags('');
  };

  const isProcessing = jobStatus && !['complete', 'failed'].includes(jobStatus.status);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="upload-page-title">
              Generate Documentation
            </h1>
            <p className="text-slate-500 text-sm">Upload one or more files (and optionally a repository) — merged into a single document</p>
          </div>
        </div>

        {/* Job Status Card */}
        <JobProgressTracker
          jobStatus={jobStatus}
          jobElapsedMin={jobElapsedMin}
          isProcessing={isProcessing}
          onViewDocs={() => navigate(`/projects/${selectedProject}`)}
          onReset={resetJob}
        />

        {/* Main form - hidden when processing */}
        {!isProcessing && jobStatus?.status !== 'complete' && (
          <>
            {/* Project Selection */}
            <Card className="mb-6 border-slate-200">
              <CardHeader>
                <CardTitle className="text-base font-['Plus_Jakarta_Sans']">Target Project</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer</Label>
                    <Select value={selectedCustomer} onValueChange={(v) => { setSelectedCustomer(v); setSelectedProject(''); }}>
                      <SelectTrigger data-testid="upload-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Project *</Label>
                    <Select value={selectedProject} onValueChange={setSelectedProject}>
                      <SelectTrigger data-testid="upload-project-select">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredProjects.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Documentation Title *</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., 3PL Warehouse Integration"
                    data-testid="upload-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tags (comma-separated)</Label>
                  <Input
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="e.g., sap, b1if, inventory"
                    data-testid="upload-tags-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Upload Method Tabs */}
            <Tabs defaultValue="file" className="space-y-4">
              <TabsList className="grid w-full grid-cols-5 h-12">
                <TabsTrigger value="file" className="flex items-center gap-2" data-testid="tab-file-upload">
                  <Upload className="h-4 w-4" />
                  File Upload
                </TabsTrigger>
                <TabsTrigger value="azure" className="flex items-center gap-2" data-testid="tab-azure-devops">
                  <Cloud className="h-4 w-4" />
                  Azure DevOps
                </TabsTrigger>
                <TabsTrigger value="github" className="flex items-center gap-2" data-testid="tab-github">
                  <GitBranch className="h-4 w-4" />
                  GitHub
                </TabsTrigger>
                <TabsTrigger value="bitbucket" className="flex items-center gap-2" data-testid="tab-bitbucket">
                  <GitBranch className="h-4 w-4" />
                  Bitbucket
                </TabsTrigger>
                <TabsTrigger value="gitlab" className="flex items-center gap-2" data-testid="tab-gitlab">
                  <GitBranch className="h-4 w-4" />
                  GitLab
                </TabsTrigger>
              </TabsList>

              {/* File Upload Tab — many files/zips (+ an optional repo) into ONE document */}
              <TabsContent value="file">
                <FileUploadTab
                  multiple
                  file={files}
                  setFile={setFiles}
                  onUpload={handleFileUpload}
                  hint="ZIP (nested zips supported), C#, Python, SQL, XML, JSON, Config files — select as many as you need (max 100MB each)"
                  disabled={(files.length === 0 && !multiRepoUrl.trim()) || !selectedProject || !title}
                >
                  {/* Optional repository, merged into the same document */}
                  <div className="border border-slate-200 rounded-lg">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50 rounded-lg transition-colors"
                      onClick={() => setShowMultiRepo(!showMultiRepo)}
                      data-testid="multi-repo-toggle"
                    >
                      <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                        <GitBranch className="h-4 w-4" />
                        Also include a code repository <span className="text-slate-400 font-normal">(optional)</span>
                      </span>
                      <span className="flex items-center gap-2">
                        {multiRepoUrl.trim() && (
                          <span className="text-xs font-medium text-[#2B8FD8] bg-blue-50 rounded-full px-2 py-0.5">
                            {REPO_PROVIDERS[multiRepoProvider].label} linked
                          </span>
                        )}
                        {showMultiRepo
                          ? <ChevronUp className="h-4 w-4 text-slate-400" />
                          : <ChevronDown className="h-4 w-4 text-slate-400" />}
                      </span>
                    </button>

                    {showMultiRepo && (
                      <div className="px-4 pb-4 pt-4 space-y-4 border-t border-slate-200">
                        <p className="text-xs text-slate-500">
                          The repository is cloned and analysed together with the files above — everything
                          is merged into a single documentation.
                        </p>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Provider</Label>
                            <Select value={multiRepoProvider} onValueChange={setMultiRepoProvider}>
                              <SelectTrigger data-testid="multi-repo-provider-select">
                                <SelectValue placeholder="Select provider" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(REPO_PROVIDERS).map(([value, cfg]) => (
                                  <SelectItem key={value} value={value}>{cfg.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <GitBranch className="h-4 w-4" />
                              Branch <span className="text-slate-400 font-normal">(optional)</span>
                            </Label>
                            <Input
                              value={multiRepoBranch}
                              onChange={(e) => setMultiRepoBranch(e.target.value)}
                              placeholder="e.g. main, develop, feature/my-branch"
                              data-testid="multi-repo-branch-input"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Link className="h-4 w-4" />
                            Repository URL
                          </Label>
                          <Input
                            value={multiRepoUrl}
                            onChange={(e) => setMultiRepoUrl(e.target.value)}
                            placeholder={REPO_PROVIDERS[multiRepoProvider].urlPlaceholder}
                            data-testid="multi-repo-url-input"
                          />
                          <p className="text-xs text-slate-500">
                            Leave blank to document the uploaded files only
                          </p>
                        </div>

                        <div className={REPO_PROVIDERS[multiRepoProvider].needsUsername ? 'grid grid-cols-2 gap-4' : 'space-y-2'}>
                          {REPO_PROVIDERS[multiRepoProvider].needsUsername && (
                            <div className="space-y-2">
                              <Label className="flex items-center gap-2">
                                <KeyRound className="h-4 w-4" />
                                Username
                              </Label>
                              <Input
                                value={multiRepoUsername}
                                onChange={(e) => setMultiRepoUsername(e.target.value)}
                                placeholder="Your Bitbucket username"
                                data-testid="multi-repo-username-input"
                              />
                            </div>
                          )}
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <KeyRound className="h-4 w-4" />
                              {REPO_PROVIDERS[multiRepoProvider].tokenLabel}
                            </Label>
                            <Input
                              type="password"
                              value={multiRepoToken}
                              onChange={(e) => setMultiRepoToken(e.target.value)}
                              placeholder={REPO_PROVIDERS[multiRepoProvider].tokenPlaceholder}
                              data-testid="multi-repo-token-input"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-slate-500">
                          Required for private repositories only
                        </p>
                      </div>
                    )}
                  </div>
                </FileUploadTab>
              </TabsContent>

              {/* Azure DevOps Tab */}
              <TabsContent value="azure">
                <Card className="border-slate-200">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Link className="h-4 w-4" />
                        Repository URL *
                      </Label>
                      <Input
                        value={repoUrl}
                        onChange={(e) => setRepoUrl(e.target.value)}
                        placeholder="https://dev.azure.com/{org}/{project}/_git/{repo}"
                        data-testid="azure-repo-url-input"
                      />
                      <p className="text-xs text-slate-500">
                        Also supports: https://{'{org}'}.visualstudio.com/{'{project}'}/_git/{'{repo}'}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4" />
                        Personal Access Token (PAT)
                      </Label>
                      <Input
                        type="password"
                        value={patToken}
                        onChange={(e) => setPatToken(e.target.value)}
                        placeholder="Your Azure DevOps PAT with Code (Read) scope"
                        data-testid="azure-pat-input"
                      />
                      <p className="text-xs text-slate-500">
                        Required for private repos. Generate at: Azure DevOps {'>'} User Settings {'>'} Personal Access Tokens
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <GitBranch className="h-4 w-4" />
                        Branch <span className="text-slate-400 font-normal">(optional)</span>
                      </Label>
                      <Input
                        value={branch}
                        onChange={(e) => setBranch(e.target.value)}
                        placeholder="e.g. main, develop, feature/my-branch"
                        data-testid="azure-branch-input"
                      />
                      <p className="text-xs text-slate-500">
                        Leave blank to use the repository's default branch
                      </p>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Multi-Pass AI Analysis</h4>
                      <p className="text-xs text-slate-500">
                        The AI performs deep analysis in multiple passes: project structure, database models,
                        business logic, and configuration. This produces comprehensive documentation
                        matching professional standards.
                      </p>
                    </div>

                    <Button
                      className="w-full bg-[#2B8FD8] hover:bg-[#2075b3] h-12 text-base"
                      onClick={handleAzureDevOpsImport}
                      disabled={!repoUrl || !selectedProject || !title}
                      data-testid="import-azure-btn"
                    >
                      <Cloud className="h-5 w-5 mr-2" />
                      Import & Generate Documentation
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* GitHub Tab */}
              <TabsContent value="github">
                <Card className="border-slate-200">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Link className="h-4 w-4" />
                        Repository URL *
                      </Label>
                      <Input
                        value={repoUrl}
                        onChange={(e) => setRepoUrl(e.target.value)}
                        placeholder="https://github.com/{owner}/{repo}"
                        data-testid="github-repo-url-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4" />
                        Access Token (optional)
                      </Label>
                      <Input
                        type="password"
                        value={patToken}
                        onChange={(e) => setPatToken(e.target.value)}
                        placeholder="GitHub token for private repositories"
                        data-testid="github-token-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <GitBranch className="h-4 w-4" />
                        Branch <span className="text-slate-400 font-normal">(optional)</span>
                      </Label>
                      <Input
                        value={branch}
                        onChange={(e) => setBranch(e.target.value)}
                        placeholder="e.g. main, develop, feature/my-branch"
                        data-testid="github-branch-input"
                      />
                      <p className="text-xs text-slate-500">
                        Leave blank to use the repository's default branch
                      </p>
                    </div>

                    <Button
                      className="w-full bg-[#2B8FD8] hover:bg-[#2075b3] h-12 text-base"
                      onClick={handleGithubImport}
                      disabled={!repoUrl || !selectedProject || !title}
                      data-testid="import-github-btn"
                    >
                      <GitBranch className="h-5 w-5 mr-2" />
                      Import & Generate Documentation
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Bitbucket Tab */}
              <TabsContent value="bitbucket">
                <Card className="border-slate-200">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Link className="h-4 w-4" />
                        Repository URL *
                      </Label>
                      <Input
                        value={repoUrl}
                        onChange={(e) => setRepoUrl(e.target.value)}
                        placeholder="https://bitbucket.org/{workspace}/{repo}"
                        data-testid="bitbucket-repo-url-input"
                      />
                      <p className="text-xs text-slate-500">
                        Also supports Bitbucket Server: https://{'{host}'}/scm/{'{project}'}/{'{repo}'}.git
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <KeyRound className="h-4 w-4" />
                          Username
                        </Label>
                        <Input
                          value={bbUsername}
                          onChange={(e) => setBbUsername(e.target.value)}
                          placeholder="Your Bitbucket username"
                          data-testid="bitbucket-username-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <KeyRound className="h-4 w-4" />
                          App Password
                        </Label>
                        <Input
                          type="password"
                          value={patToken}
                          onChange={(e) => setPatToken(e.target.value)}
                          placeholder="Bitbucket App Password"
                          data-testid="bitbucket-app-password-input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      Required for private repos. Generate at: Bitbucket {'>'} Personal Settings {'>'} App Passwords (needs Repository Read permission)
                    </p>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <GitBranch className="h-4 w-4" />
                        Branch <span className="text-slate-400 font-normal">(optional)</span>
                      </Label>
                      <Input
                        value={branch}
                        onChange={(e) => setBranch(e.target.value)}
                        placeholder="e.g. main, develop, feature/my-branch"
                        data-testid="bitbucket-branch-input"
                      />
                      <p className="text-xs text-slate-500">
                        Leave blank to use the repository's default branch
                      </p>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Multi-Pass AI Analysis</h4>
                      <p className="text-xs text-slate-500">
                        The AI performs deep analysis in multiple passes: project structure, database models,
                        business logic, and configuration. This produces comprehensive documentation
                        matching professional standards.
                      </p>
                    </div>

                    <Button
                      className="w-full bg-[#2B8FD8] hover:bg-[#2075b3] h-12 text-base"
                      onClick={handleBitbucketImport}
                      disabled={!repoUrl || !selectedProject || !title}
                      data-testid="import-bitbucket-btn"
                    >
                      <GitBranch className="h-5 w-5 mr-2" />
                      Import & Generate Documentation
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* GitLab Tab */}
              <TabsContent value="gitlab">
                <Card className="border-slate-200">
                  <CardContent className="p-6 space-y-4">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Link className="h-4 w-4" />
                        Repository URL *
                      </Label>
                      <Input
                        value={repoUrl}
                        onChange={(e) => setRepoUrl(e.target.value)}
                        placeholder="https://gitlab.com/{owner}/{repo}"
                        data-testid="gitlab-repo-url-input"
                      />
                      <p className="text-xs text-slate-500">
                        Also supports self-hosted GitLab: https://{'{your-gitlab}'}/{'{namespace}'}/{'{repo}'}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4" />
                        Personal Access Token (optional)
                      </Label>
                      <Input
                        type="password"
                        value={patToken}
                        onChange={(e) => setPatToken(e.target.value)}
                        placeholder="GitLab personal access token"
                        data-testid="gitlab-token-input"
                      />
                      <p className="text-xs text-slate-500">
                        Required for private repos. Create at: GitLab {'>'} User Settings {'>'} Access Tokens (needs read_repository scope)
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <GitBranch className="h-4 w-4" />
                        Branch <span className="text-slate-400 font-normal">(optional)</span>
                      </Label>
                      <Input
                        value={branch}
                        onChange={(e) => setBranch(e.target.value)}
                        placeholder="e.g. main, develop, feature/my-branch"
                        data-testid="gitlab-branch-input"
                      />
                      <p className="text-xs text-slate-500">
                        Leave blank to use the repository's default branch
                      </p>
                    </div>

                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Multi-Pass AI Analysis</h4>
                      <p className="text-xs text-slate-500">
                        The AI performs deep analysis in multiple passes: project structure, database models,
                        business logic, and configuration. This produces comprehensive documentation
                        matching professional standards.
                      </p>
                    </div>

                    <Button
                      className="w-full bg-[#2B8FD8] hover:bg-[#2075b3] h-12 text-base"
                      onClick={handleGitlabImport}
                      disabled={!repoUrl || !selectedProject || !title}
                      data-testid="import-gitlab-btn"
                    >
                      <GitBranch className="h-5 w-5 mr-2" />
                      Import & Generate Documentation
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </Layout>
  );
};

export default UploadPage;
